package com.oasisfeng.invaluable.valuation.impl

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.CommitmentSnapshot
import com.oasisfeng.invaluable.valuation.AnchoredValuation
import com.oasisfeng.invaluable.valuation.LinkOwnership
import com.oasisfeng.invaluable.valuation.ValueField
import com.oasisfeng.invaluable.valuation.ValueFieldEngine
import kotlin.math.abs
import kotlin.math.min

/**
 * Valuation by **one-shot conserved attribution with counterparty settlement** — [ConservedFlow]'s pricing model with
 * its incentive loop closed (audit: `docs/reference/valuation-engine-incentive-audit-and-settledflow.md`).
 *
 * A coexisting candidate (it does not modify or depend on [ConservedFlow]/[MarginalFlow]); the base is identical to
 * [ConservedFlow] — price-free par parcels, one attribution pass, `p_a = A_a / I_a`, peak-normalized ray, global
 * self-consistency `Σ p_a·I_a = Σ A_a` (the §9m-adjudicated trilemma resolution stands unchanged). Two additions:
 *
 *  - **Rule M — bargain against the strongest upstream** ([ruleMWeights]). Rule G's adjacent-pair ratio is structurally
 *    *discontinuous*: a 1-unit self-buffer interposed under a 1000-stake whale re-routes the split and multiplies the
 *    committer's price ~10.8× (audit §2.1, gate 1). Rule M keeps the same pairwise settle-or-continue telescoping but
 *    prices each step against the strongest *remaining* upstream stake, `ρ_j = d_j / (d_j + max_{i>j} d_i)` — a lattice
 *    operation, not a constant. Thin buffers no longer shield (the max sees past them, continuously), while every
 *    chain whose stakes do not increase upstream — including all equal chains, e.g. the design-doc §4.4 hand-calc
 *    `A = [175, 75, 50]` — is split *identically* to Rule G, so the calibrated gates are untouched.
 *
 *  - **Per-locus zero-sum settlement** ([Domain.settle]) — the charter §5.2 counterparty organ. Attribution alone gives
 *    every commitment risk-free par: opposing is free parking plus damage, backing never beats self-posting, and a
 *    refuted narrative keeps its collected rent (audit gates 2–6, all red on [ConservedFlow]). After attribution, each
 *    unit of opposed gross mass is settled exactly once, at the *lowest* link where the two sides meet:
 *    `newDispute(L) = min(T⁺(L),T⁻(L)) − Σ_children min(T⁺,T⁻)` (≥ 0 by superadditivity of `min`), transferred
 *    `t = newDispute·|τ|` with `τ = V(L)/(T⁺(L)+T⁻(L))` from the losing-side to the winning-side *landed* claims of
 *    `L`'s subtree, pro-rata, capacity-tracked so no claim ever pays out more than it landed. Zero-sum ⇒ global
 *    conservation is untouched; `τ` continuous ⇒ zero-net disputes stay neutral and scale invariance holds; channel
 *    mirror ⇒ sign-reflection symmetry holds; no constants. Support thereby becomes a long position and opposition a
 *    short: the correct side of a dispute earns from the wrong side, parked stakes and collected ancestor rent are
 *    contestable (charter scenarios 4.5 and 5 get teeth), and late same-direction herders only dilute a fixed pot.
 *
 * **Credit vs wealth layering.** [ValueField.ownership] and [AnchoredValuation.valueOf] report the *pre-settlement*
 * attribution: they answer "whose judgment is credited with this value" (`Σ share + residual == 1` stays exact), while
 * settlement only moves *wealth* (holdings, hence prices). Settlement is recomputed from scratch each snapshot —
 * mark-to-market, not confiscation: a minority-yet-correct position reads as a loss only until corroborating
 * commitments flip the subtree consensus, whereupon the same law pays it (self-correction, charter scenario 5).
 *
 * **Boundary axiom (audit §4.3).** Par units make the *incentive* claims above conditional on protocol-level issuance
 * scarcity (per-actor write budget) and non-zero identity cost (Credential layer): without scarcity, displayed values
 * can be shouted arbitrarily large and sybil counterparties are free. The *mathematical* invariants (conservation,
 * scale/order/aggregation/ghost/sign, claim ≤ 100%, non-negative prices) hold regardless.
 */
object SettledFlow : ValueFieldEngine {

    override fun valuate(snapshot: CommitmentSnapshot): ValueField = Domain.from(snapshot).solve()

    override fun toString(): String = "SettledFlow"

    private const val EPS = 1e-12

    /**
     * Smallest fraction of the peak price an actor may hold and still serve as a measuring frame — a presentation-layer
     * guard (not an economic constant): framing divides every price by the anchor's own, so a negligible anchor would
     * explode every reading. Like [ConservedFlow], every committer keeps at least its creator share at par, so this
     * only trips on pathological stake ratios (bounded anyway under a protocol write budget).
     */
    private const val MIN_FRAME_ANCHOR_FRACTION = 1e-6

    /** One actor's signed-magnitude commitment into a link, issuer pre-indexed. */
    private class Contribution(val actor: Int, val absAmount: Double)

    private class Attribution(
        /** `h[a] = h⁺(a) + h⁻(a)`: total conserved value attributed to actor `a` (both channels are gross-positive). */
        val holding: DoubleArray,
        /** `landed[L][a]`: net value (support positive, opposition negative) claimed by `a` *at* link `L`. */
        val landed: Map<NestedLink, DoubleArray>,
    )

    /** Exact, price-free structure extracted once from a snapshot (identical to [ConservedFlow]'s structure layer). */
    private class Domain private constructor(
        val actors: List<Actor>,
        val actorIndex: Map<Actor, Int>,
        val issuanceQ: List<Quantity>,
        val issuance: DoubleArray,
        val investedLinks: List<NestedLink>,
        val posByLink: Map<NestedLink, List<Contribution>>,
        val negByLink: Map<NestedLink, List<Contribution>>,
        /** Each committed link's invested ancestors, creator-first (self, then nearest invested ancestor, …). */
        val ancestorsInclSelf: Map<NestedLink, List<NestedLink>>,
        /** Inverse of [ancestorsInclSelf]: every committed link in `L`'s attribution subtree (including `L`). */
        val subtreeOf: Map<NestedLink, List<NestedLink>>,
    ) {
        val n: Int get() = actors.size

        /** Attribute the par intrinsic (Rule M), settle disputes (zero-sum), then price by holding per unit issued. */
        fun solve(): ValueField {
            val dPos = channelStakes(posByLink)
            val dNeg = channelStakes(negByLink)
            val attribution = attribute(dPos, dNeg)
            val settlement = settle(attribution, dPos, dNeg)

            val priceRay = DoubleArray(n) {
                if (issuance[it] > 0.0) ((attribution.holding[it] + settlement[it]) / issuance[it]).coerceAtLeast(0.0) else 0.0
            }
            // Anchor-free internal normalization: scale by the peak so the ray stays bounded; never privilege an actor.
            val scale = priceRay.maxOrNull()?.takeIf { it > EPS } ?: 1.0
            for (i in 0 until n) priceRay[i] = (priceRay[i] / scale).coerceAtLeast(0.0)

            // Credit layer, deliberately pre-settlement (see class KDoc): shares partition the subtree's gross
            // committed judgment (charter §3.7) — landed magnitude over gross mass, so they stay in [0, 1] and
            // `Σ share + residual == 1` holds identically on any mix of support and opposition.
            val netLocal = investedLinks.associateWith { (dPos[it] ?: 0.0) - (dNeg[it] ?: 0.0) }
            val linkValue = investedLinks.associateWith { link -> subtreeOf.getValue(link).sumOf { netLocal.getValue(it) } }
            val grossLocal = investedLinks.associateWith { (dPos[it] ?: 0.0) + (dNeg[it] ?: 0.0) }
            val ownership = investedLinks.associateWith { link ->
                val gross = subtreeOf.getValue(link).sumOf { grossLocal.getValue(it) } // > 0: invested links carry mass
                val shares = buildMap {
                    for (i in 0 until n) {
                        val claimed = subtreeOf.getValue(link).sumOf { abs(attribution.landed[it]?.get(i) ?: 0.0) }
                        if (claimed != 0.0) put(actors[i], claimed / gross)
                    }
                }
                OwnershipData(shares, residual = 1.0 - shares.values.sum())
            }
            return Field(actors.toSet(), actorIndex, issuanceQ, priceRay, linkValue, ownership)
        }

        /** Distribute every link's per-channel par intrinsic (`Σ|q|`) up its invested-ancestor chain (Rule M). */
        private fun attribute(dPos: Map<NestedLink, Double>, dNeg: Map<NestedLink, Double>): Attribution {
            val holding = DoubleArray(n)
            val landed = HashMap<NestedLink, DoubleArray>()
            fun landedOf(link: NestedLink) = landed.getOrPut(link) { DoubleArray(n) }

            for (origin in investedLinks) {
                distributeParcel(origin, posByLink, dPos, sign = +1.0, holding, ::landedOf)
                distributeParcel(origin, negByLink, dNeg, sign = -1.0, holding, ::landedOf)
            }
            return Attribution(holding, landed)
        }

        private fun distributeParcel(
            origin: NestedLink,
            byLink: Map<NestedLink, List<Contribution>>,
            stake: Map<NestedLink, Double>,
            sign: Double,
            holding: DoubleArray,
            landedOf: (NestedLink) -> DoubleArray,
        ) {
            val parcel = stake[origin] ?: 0.0
            if (parcel <= 0.0) return
            val chain = ancestorsInclSelf.getValue(origin).filter { (stake[it] ?: 0.0) > 0.0 }
            val weights = ruleMWeights(chain.map { stake.getValue(it) })
            for (j in chain.indices) {
                val weight = weights[j]
                if (weight <= 0.0) continue
                val node = chain[j]
                val pool = stake.getValue(node)
                if (pool <= 0.0) continue
                val toNode = weight * parcel
                val landedNode = landedOf(node)
                for (contribution in byLink.getValue(node)) {
                    val fraction = contribution.absAmount / pool
                    val amount = toNode * fraction
                    holding[contribution.actor] += amount
                    landedNode[contribution.actor] += sign * amount
                }
            }
        }

        /**
         * Per-locus zero-sum counterparty settlement over the landed attribution (class KDoc, audit §4.2).
         *
         * Each unit of opposed gross mass joins a dispute exactly once, at the lowest link where support and opposition
         * meet; the dispute's direction and intensity come from that link's whole-subtree consensus `τ`; payers are the
         * losing-side landed claims within the subtree, pro-rata over their *remaining* capacity (so a claim never pays
         * more than it landed, and deeper disputes are charged first), receivers the winning-side landed claims pro-rata.
         * Returns the per-actor wealth delta; Σ delta == 0.
         */
        private fun settle(attribution: Attribution, dPos: Map<NestedLink, Double>, dNeg: Map<NestedLink, Double>): DoubleArray {
            val delta = DoubleArray(n)
            if (dPos.isEmpty() || dNeg.isEmpty()) return delta

            val tPos = HashMap<NestedLink, Double>(); val tNeg = HashMap<NestedLink, Double>()
            for (link in investedLinks) {
                val subtree = subtreeOf.getValue(link)
                tPos[link] = subtree.sumOf { dPos[it] ?: 0.0 }
                tNeg[link] = subtree.sumOf { dNeg[it] ?: 0.0 }
            }
            // Dispute mass newly joined at L: its own subtree overlap minus what already met inside child subtrees.
            val newDispute = HashMap<NestedLink, Double>()
            for (link in investedLinks) newDispute[link] = min(tPos.getValue(link), tNeg.getValue(link))
            for (link in investedLinks) {
                val parent = ancestorsInclSelf.getValue(link).getOrNull(1) ?: continue
                newDispute[parent] = newDispute.getValue(parent) - min(tPos.getValue(link), tNeg.getValue(link))
            }

            val remaining = HashMap<NestedLink, DoubleArray>()
            for ((link, landed) in attribution.landed) remaining[link] = DoubleArray(n) { abs(landed[it]) }

            // Deepest first, so every dispute settles at the lowest meeting locus before ancestors see the remainder.
            for (locus in investedLinks.sortedByDescending { ancestorsInclSelf.getValue(it).size }) {
                val joined = newDispute.getValue(locus)
                if (joined <= EPS) continue
                val pos = tPos.getValue(locus); val neg = tNeg.getValue(locus)
                val tau = (pos - neg) / (pos + neg)
                if (abs(tau) <= EPS) continue // zero-net dispute: perfectly contested mass moves nothing (charter §3.4)
                val winnerSign = if (tau > 0.0) 1.0 else -1.0

                val subtree = subtreeOf.getValue(locus)
                var loserCapacity = 0.0; var winnerStake = 0.0
                for (link in subtree) {
                    val landed = attribution.landed[link] ?: continue
                    val capacity = remaining.getValue(link)
                    for (i in 0 until n) {
                        val net = landed[i] * winnerSign
                        if (net > 0.0) winnerStake += abs(landed[i]) else if (net < 0.0) loserCapacity += capacity[i]
                    }
                }
                val transfer = min(joined * abs(tau), loserCapacity)
                if (transfer <= EPS || winnerStake <= EPS) continue
                for (link in subtree) {
                    val landed = attribution.landed[link] ?: continue
                    val capacity = remaining.getValue(link)
                    for (i in 0 until n) {
                        val net = landed[i] * winnerSign
                        if (net > 0.0) delta[i] += transfer * abs(landed[i]) / winnerStake
                        else if (net < 0.0) {
                            val pay = transfer * capacity[i] / loserCapacity
                            capacity[i] -= pay
                            delta[i] -= pay
                        }
                    }
                }
            }
            return delta
        }

        /** Per-channel par stake of each link: the price-free sum of committed magnitudes `Σ|q|`. */
        private fun channelStakes(byLink: Map<NestedLink, List<Contribution>>): Map<NestedLink, Double> =
            byLink.mapValues { (_, contributions) -> contributions.sumOf { it.absAmount } }

        companion object {
            fun from(snapshot: CommitmentSnapshot): Domain {
                val actors = snapshot.issuers().toList()
                val actorIndex = actors.withIndex().associate { (index, actor) -> actor to index }

                val issuanceQ = MutableList(actors.size) { Quantity.ZERO }
                val pos = HashMap<NestedLink, MutableList<Contribution>>()
                val neg = HashMap<NestedLink, MutableList<Contribution>>()
                val investedOrder = LinkedHashSet<NestedLink>()

                for (commitment in snapshot.commitments) {
                    val issuer = actorIndex.getValue(commitment.issuer)
                    val quantity = commitment.quantity
                    val amount = quantity.toDouble()
                    issuanceQ[issuer] = issuanceQ[issuer] + quantity.abs()
                    if (amount == 0.0) continue
                    val target = commitment.targetLink
                    investedOrder += target
                    val contribution = Contribution(issuer, abs(amount))
                    if (amount > 0.0) pos.getOrPut(target) { mutableListOf() } += contribution
                    else neg.getOrPut(target) { mutableListOf() } += contribution
                }

                val investedLinks = investedOrder.toList()
                val investedSet = investedOrder
                val ancestorsInclSelf = investedLinks.associateWith { link ->
                    buildList {
                        add(link)
                        for (ancestor in link.ancestors()) if (ancestor in investedSet) add(ancestor)
                    }
                }
                val subtreeOf = HashMap<NestedLink, MutableList<NestedLink>>()
                for (link in investedLinks) for (ancestor in ancestorsInclSelf.getValue(link)) {
                    subtreeOf.getOrPut(ancestor) { mutableListOf() } += link
                }

                return Domain(
                    actors = actors,
                    actorIndex = actorIndex,
                    issuanceQ = issuanceQ,
                    issuance = DoubleArray(actors.size) { issuanceQ[it].toDouble() },
                    investedLinks = investedLinks,
                    posByLink = pos,
                    negByLink = neg,
                    ancestorsInclSelf = ancestorsInclSelf,
                    subtreeOf = subtreeOf,
                )
            }
        }
    }

    /** Frame-free shares + residual for one link. */
    private class OwnershipData(val shares: Map<Actor, Double>, val residual: Double)

    /** Immutable anchor-free field backed by the settled price ray. */
    private class Field(
        override val actors: Set<Actor>,
        private val actorIndex: Map<Actor, Int>,
        private val issuanceQ: List<Quantity>,
        private val priceRay: DoubleArray,
        private val linkValue: Map<NestedLink, Double>,
        private val ownership: Map<NestedLink, OwnershipData>,
    ) : ValueField {

        override fun issuance(actor: Actor): Quantity = actorIndex[actor]?.let { issuanceQ[it] } ?: Quantity.ZERO

        override fun ownership(link: NestedLink): LinkOwnership =
            LinkOwnershipView(link, ownership[link] ?: OwnershipData(emptyMap(), 0.0))

        override fun relativeTo(anchor: Actor): AnchoredValuation {
            val index = actorIndex[anchor] ?: throw IllegalArgumentException("Anchor $anchor is not valued in this field.")
            val unit = priceRay[index]
            val peak = priceRay.maxOrNull() ?: 0.0
            require(peak > EPS && unit >= peak * MIN_FRAME_ANCHOR_FRACTION) {
                "Anchor $anchor has a degenerate price ($unit, negligible vs the peak $peak) and cannot frame a valuation; pick a non-degenerate anchor."
            }
            return Frame(this, anchor, unit)
        }

        fun rawPrice(actor: Actor): Double = actorIndex[actor]?.let { priceRay[it] } ?: 0.0
        fun rawValue(link: NestedLink): Double = linkValue[link] ?: 0.0
        fun issuanceDouble(actor: Actor): Double = actorIndex[actor]?.let { issuanceQ[it].toDouble() } ?: 0.0
    }

    private class LinkOwnershipView(override val link: NestedLink, private val data: OwnershipData) : LinkOwnership {
        override val claimants: Set<Actor> get() = data.shares.keys
        override fun shareOf(actor: Actor): Double = data.shares[actor] ?: 0.0
        override val residual: Double get() = data.residual
    }

    /** A frame: divides the anchor-free ray by the anchor's own price so the anchor reads as 1. */
    private class Frame(override val field: Field, override val anchor: Actor, private val unit: Double) : AnchoredValuation {
        override fun priceOf(actor: Actor): Double = (field.rawPrice(actor) / unit).coerceAtLeast(0.0)
        override fun assetsOf(actor: Actor): Double = priceOf(actor) * field.issuanceDouble(actor)
        override fun valueOf(link: NestedLink): Double = field.rawValue(link) / unit
    }

    /**
     * Rule M — pairwise settle-or-continue against the **strongest remaining upstream** stake.
     *
     * For a chain of channel stakes `[d_0 (creator), d_1, …, d_k]`, the parcel either settles at node `j` or continues
     * upward in proportion `ρ_j = d_j / (d_j + max_{i>j} d_i)`, the top node absorbing the remainder. Weights telescope
     * to exactly 1 (distributed, never cloned) and the ratio is a pure structural quantity (`max` is a lattice
     * operation, not a constant). Against Rule G's adjacent-pair `d_{j+1}` this is the minimal continuity fix: an
     * ε-stake buffer cannot shield a fat stake from a whale above it (the `max` sees past it, and the split varies
     * continuously in ε), while any chain whose stakes do not increase upstream — equal chains included — yields
     * weights identical to Rule G, e.g. `[½, ¼, ⅛, …]`.
     */
    internal fun ruleMWeights(stakes: List<Double>): DoubleArray {
        val size = stakes.size
        val weights = DoubleArray(size)
        if (size == 0) return weights
        val maxAbove = DoubleArray(size) // strongest stake strictly above each node
        for (j in size - 2 downTo 0) maxAbove[j] = maxOf(stakes[j + 1], maxAbove[j + 1])
        var remaining = 1.0
        for (j in 0 until size - 1) {
            val denom = stakes[j] + maxAbove[j]
            val rho = if (denom > 0.0) stakes[j] / denom else 0.0
            weights[j] = remaining * rho
            remaining *= (1.0 - rho)
        }
        weights[size - 1] = remaining
        return weights
    }
}
